import * as z from 'zod';
import type { ZodOpenApiPathsObject } from 'zod-openapi';
import { problemResponse } from './common.js';

/** Q-points (spec §5, docs/features/q-points/SRS.md §6.1). Percent in the API, basis points inside. */
const percent = z.number().min(0).max(100).meta({ description: 'Percent, at most 2 decimals' });

export const LoyaltyTermsSchema = z
  .object({
    enabled: z.boolean().meta({ description: 'The program switch' }),
    accrualPercent: percent.meta({
      description: 'Share of the paid goods amount earned as points',
    }),
    pointValue: z
      .number()
      .int()
      .min(1)
      .max(100_000)
      .meta({ description: 'Kopecks one point is worth when spent' }),
    maxRedeemPercent: percent
      .min(0.01)
      .nullable()
      .meta({ description: 'Max share of an order points may pay; null = no cap' }),
    expiryMonths: z
      .number()
      .int()
      .min(1)
      .max(120)
      .nullable()
      .meta({ description: 'Lifetime after activation, calendar months; null = never expire' }),
  })
  .meta({ id: 'LoyaltyTerms' });

export const LoyaltySettingsVersionSchema = LoyaltyTermsSchema.extend({
  id: z.uuid(),
  version: z.number().int(),
  validFrom: z.iso.datetime(),
  reason: z.string(),
  createdBy: z.uuid().nullable(),
  createdByName: z.string().nullable(),
}).meta({ id: 'LoyaltySettingsVersion' });

export const UpdateLoyaltySettingsSchema = LoyaltyTermsSchema.extend({
  reason: z.string().min(1).max(500),
  dryRun: z.boolean().default(false),
}).meta({ id: 'UpdateLoyaltySettings' });

export const LoyaltySettingsChangeSchema = z
  .object({
    applied: z.boolean(),
    version: LoyaltySettingsVersionSchema.nullable(),
    conversion: z
      .object({
        customers: z.number().int(),
        pointsBefore: z.number().int(),
        pointsAfter: z.number().int(),
      })
      .nullable()
      .meta({ description: 'Set when the point value changes: every balance is converted' }),
  })
  .meta({ id: 'LoyaltySettingsChange' });

export const LoyaltyEntrySchema = z
  .object({
    id: z.uuid(),
    kind: z.enum(['accrue', 'redeem', 'reverse', 'expire', 'adjust', 'convert']),
    points: z.number().int(),
    status: z.enum(['pending', 'active', 'cancelled']),
    orderId: z.uuid().nullable(),
    expiresAt: z.iso.datetime().nullable(),
    activatedAt: z.iso.datetime().nullable(),
    cancelledAt: z.iso.datetime().nullable(),
    reason: z.string().nullable(),
    createdBy: z.uuid().nullable(),
    createdByName: z.string().nullable(),
    createdAt: z.iso.datetime(),
  })
  .meta({ id: 'LoyaltyEntry' });

const balances = {
  spendable: z.number().int().meta({ description: 'Points the customer can spend now' }),
  pending: z.number().int().meta({ description: 'Earned, spendable once the order completes' }),
  debt: z
    .number()
    .int()
    .meta({ description: 'Points owed after a return; paid off by new points' }),
  pointValue: z.number().int().meta({ description: 'Kopecks one point is worth now' }),
  nearestExpiry: z
    .object({ at: z.iso.datetime(), points: z.number().int() })
    .nullable()
    .meta({ description: 'The next batch of spendable points to expire' }),
};

export const LoyaltySummarySchema = z
  .object({
    customerId: z.uuid(),
    ...balances,
    balance: z.number().int().meta({ description: 'Σ active entries (spendable − debt)' }),
    lots: z.array(
      z.object({
        entryId: z.uuid(),
        orderId: z.uuid().nullable(),
        kind: z.enum(['accrue', 'adjust']),
        points: z.number().int(),
        remaining: z.number().int(),
        activatedAt: z.iso.datetime().nullable(),
        expiresAt: z.iso.datetime().nullable(),
      }),
    ),
    entries: z.array(LoyaltyEntrySchema).meta({ description: 'Newest first' }),
  })
  .meta({ id: 'LoyaltySummary' });

export const LoyaltyAdjustmentSchema = z
  .object({
    points: z
      .number()
      .int()
      .refine((n) => n !== 0, 'not zero'),
    reason: z.string().min(1).max(500),
  })
  .meta({ id: 'LoyaltyAdjustment' });

/** What the store's My Account page shows (no staff names, no reasons). */
export const StoreLoyaltySummarySchema = z
  .object({
    ...balances,
    history: z.array(
      z.object({
        kind: z.enum(['accrue', 'redeem', 'reverse', 'expire', 'adjust', 'convert']),
        points: z.number().int(),
        status: z.enum(['pending', 'active', 'cancelled']),
        orderId: z.uuid().nullable(),
        expiresAt: z.iso.datetime().nullable(),
        createdAt: z.iso.datetime(),
      }),
    ),
  })
  .meta({ id: 'StoreLoyaltySummary' });

export const LoyaltyOutstandingSchema = z
  .object({
    asOf: z.iso.datetime(),
    active: z.number().int().meta({ description: 'Spendable points net of debt' }),
    pending: z.number().int(),
    customers: z.number().int(),
    pointValue: z.number().int(),
    activeValue: z.number().int().meta({ description: 'Kopecks' }),
    pendingValue: z.number().int().meta({ description: 'Kopecks' }),
  })
  .meta({ id: 'LoyaltyOutstanding' });

export const LoyaltyMovementSchema = z
  .object({
    from: z.iso.datetime(),
    to: z.iso.datetime(),
    rows: z.array(
      z.object({
        kind: z.enum(['accrue', 'redeem', 'reverse', 'expire', 'adjust', 'convert']),
        sign: z.enum(['plus', 'minus']),
        points: z.number().int(),
        entries: z.number().int(),
      }),
    ),
  })
  .meta({ id: 'LoyaltyMovement' });

export type LoyaltyTermsDto = z.infer<typeof LoyaltyTermsSchema>;
export type LoyaltySettingsVersionDto = z.infer<typeof LoyaltySettingsVersionSchema>;
export type UpdateLoyaltySettingsRequest = z.output<typeof UpdateLoyaltySettingsSchema>;
export type LoyaltySettingsChangeDto = z.infer<typeof LoyaltySettingsChangeSchema>;
export type LoyaltyEntryDto = z.infer<typeof LoyaltyEntrySchema>;
export type LoyaltySummaryDto = z.infer<typeof LoyaltySummarySchema>;
export type LoyaltyAdjustmentRequest = z.infer<typeof LoyaltyAdjustmentSchema>;
export type StoreLoyaltySummaryDto = z.infer<typeof StoreLoyaltySummarySchema>;
export type LoyaltyOutstandingDto = z.infer<typeof LoyaltyOutstandingSchema>;
export type LoyaltyMovementDto = z.infer<typeof LoyaltyMovementSchema>;

const json = <T extends z.ZodType>(schema: T) => ({ 'application/json': { schema } });
const id = z.object({ id: z.uuid() });
const staff = {
  '401': problemResponse('Not signed in'),
  '403': problemResponse('Missing permission'),
};

export const loyaltyPaths: ZodOpenApiPathsObject = {
  '/api/v1/loyalty/settings': {
    get: {
      summary: 'Q-points settings: every version, newest first',
      responses: {
        '200': { description: 'OK', content: json(z.array(LoyaltySettingsVersionSchema)) },
        ...staff,
      },
    },
    post: {
      summary:
        'New settings version (reason required). New orders take it; placed orders keep theirs. A point value change converts every balance; dryRun previews it',
      requestBody: { required: true, content: json(UpdateLoyaltySettingsSchema) },
      responses: {
        '200': { description: 'Saved or previewed', content: json(LoyaltySettingsChangeSchema) },
        '400': problemResponse('Invalid values'),
        ...staff,
      },
    },
  },
  '/api/v1/customers/{id}/loyalty': {
    get: {
      summary: "A customer's Q-points: balances, batches with due dates, every entry",
      requestParams: { path: id },
      responses: {
        '200': { description: 'OK', content: json(LoyaltySummarySchema) },
        ...staff,
        '404': problemResponse('No such customer'),
      },
    },
  },
  '/api/v1/customers/{id}/loyalty/adjustments': {
    post: {
      summary: 'Manual correction (permission loyalty.adjust, reason required, audited)',
      requestParams: { path: id },
      requestBody: { required: true, content: json(LoyaltyAdjustmentSchema) },
      responses: {
        '200': { description: 'Posted', content: json(LoyaltySummarySchema) },
        '400': problemResponse('Invalid correction'),
        ...staff,
        '404': problemResponse('No such customer'),
      },
    },
  },
  '/api/v1/loyalty/customers/{id}/summary': {
    get: {
      summary: "For the store (signed): a customer's balances and history for My Account",
      requestParams: { path: id },
      responses: {
        '200': { description: 'OK', content: json(StoreLoyaltySummarySchema) },
        '401': problemResponse('Unsigned or bad signature'),
        '404': problemResponse('No such customer'),
      },
    },
  },
  '/api/v1/loyalty/reports/outstanding': {
    get: {
      summary: 'Outstanding points (spendable + pending) at a moment, in points and kopecks',
      requestParams: { query: z.object({ asOf: z.iso.datetime().optional() }) },
      responses: {
        '200': { description: 'OK', content: json(LoyaltyOutstandingSchema) },
        ...staff,
      },
    },
  },
  '/api/v1/loyalty/reports/movement': {
    get: {
      summary: 'Points earned, spent, returned, expired and corrected in a period',
      requestParams: { query: z.object({ from: z.iso.datetime(), to: z.iso.datetime() }) },
      responses: { '200': { description: 'OK', content: json(LoyaltyMovementSchema) }, ...staff },
    },
  },
};
